import { useNavigate } from 'react-router-dom';
import { CapaEvento } from '@/components/ui/CapaEvento';

interface TopoEventoProps {
  /** Arte em pé (celular). */
  imagemMobile: string | null;
  /** Arte deitada (computador). */
  imagemDesktop: string | null;
  nome: string;
  categoria: string;
  seloCancelado: boolean;
  seloEncerrado: boolean;
  onCompartilhar: () => void;
  onAbrirArte?: () => void;
}

// Topo do evento — REGRA ÚNICA das artes:
// - A arte NUNCA é cortada: vai inteira, e a caixa segue a proporção REAL do
//   cartaz (img w-full h-auto), com teto de 75vh. Só passa para
//   object-contain + fundo desfocado quando estoura esse teto.
// - Enquanto carrega, reserva 4:5 com fundo cinza para não pular a tela.
// - Celular: arte em pé ocupando a largura toda. Computador: arte deitada.
// - Tocar no cartaz abre em tela cheia (fundo preto, fecha com X).
// - Sem arte: degradê da marca com o nome por cima.
export default function TopoEvento({
  imagemMobile,
  imagemDesktop,
  nome,
  categoria,
  seloCancelado,
  seloEncerrado,
  onCompartilhar,
  onAbrirArte,
}: TopoEventoProps) {
  const navigate = useNavigate();
  const mostrarCategoria = categoria.trim().length > 0;

  const doCelular = imagemMobile || imagemDesktop;
  const doComputador = imagemDesktop || imagemMobile;

  const botaoRedondo =
    'aperta flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md';

  // -------- COM arte: cartaz inteiro + fundo desfocado, sem texto por cima --------
  if (doCelular && doComputador) {
    return (
      <div className="relative w-full overflow-hidden bg-foreground-950">
        {/* fundo: a própria arte desfocada — nunca sobra faixa vazia */}
        <img
          src={doComputador}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-110 object-cover object-center opacity-60 blur-xl"
          decoding="async"
        />

        {/* cartaz inteiro: a caixa segue o formato real da arte (teto 75vh) */}
        <div className="relative mx-auto w-full max-w-[1120px]">
          <CapaEvento
            mobile={imagemMobile}
            desktop={imagemDesktop}
            alt={nome}
            natural
            prioridade
            className="!rounded-none"
            onAbrir={onAbrirArte}
          />
        </div>

        {/* véu curto no topo, só para os botões redondos ficarem legíveis
            (não pode escurecer/"comer" a parte de cima do cartaz) */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/40 to-transparent" />

        {/* Botões redondos por cima de qualquer arte */}
        <div
          className="absolute inset-x-0 top-0 flex items-center justify-between px-4"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Voltar"
            className={botaoRedondo}
          >
            <i className="ri-arrow-left-line text-xl" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onCompartilhar}
            aria-label="Compartilhar"
            className={botaoRedondo}
          >
            <i className="ri-share-forward-line text-xl" aria-hidden />
          </button>
        </div>

        {onAbrirArte && (
          <button
            type="button"
            onClick={onAbrirArte}
            className="aperta absolute bottom-6 right-4 inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-black/55 px-3.5 py-2 text-[12px] font-semibold text-white backdrop-blur-md"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-fullscreen-line" aria-hidden />
            </span>
            Ver arte
          </button>
        )}
      </div>
    );
  }

  // -------- SEM arte: degradê da marca com o nome por cima --------
  return (
    <div className="relative h-[300px] w-full overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800" />

      {/* Película: escurece de baixo para cima para o título ficar legível */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, rgba(15,23,42,.85) 0%, rgba(15,23,42,.42) 45%, rgba(15,23,42,0) 72%)',
        }}
      />
      {/* leve véu no topo para os botões redondos */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/40 to-transparent" />

      <div
        className="absolute inset-x-0 top-0 flex items-center justify-between px-5"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
          className={botaoRedondo}
        >
          <i className="ri-arrow-left-line text-xl" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onCompartilhar}
          aria-label="Compartilhar"
          className={botaoRedondo}
        >
          <i className="ri-share-forward-line text-xl" aria-hidden />
        </button>
      </div>

      <div className="absolute inset-x-0 bottom-0 px-5 pb-9">
        {mostrarCategoria && (
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
            {categoria}
          </p>
        )}
        <h1
          className="mt-1 line-clamp-2 text-[30px] font-extrabold leading-[1.1] text-white"
          style={{ textShadow: '0 1px 14px rgba(0,0,0,.5)' }}
        >
          {nome}
        </h1>
        {seloCancelado && (
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-[12px] font-bold text-danger-700">
            <i className="ri-close-circle-line" aria-hidden />
            Evento cancelado
          </span>
        )}
        {seloEncerrado && !seloCancelado && (
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-[12px] font-bold text-foreground-700">
            <i className="ri-time-line" aria-hidden />
            Já aconteceu
          </span>
        )}
      </div>
    </div>
  );
}